﻿import { Coordinate } from 'ol/coordinate';
import { getCenter } from 'ol/extent';
import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import LinearRing from 'ol/geom/LinearRing';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from "ol/geom/MultiPolygon";

import { BaseGeometryExtension, GeometryExtensionUtilities } from './BaseGeometryExtension';
import { DEGREES_360 } from '../../utilities/DateLineUtilities';

export class MultiPolygonExtension extends BaseGeometryExtension<MultiPolygon> {

    public getCoordinates() {
        return this.geometry.getCoordinates();
    }
    public setCoordinates(coordinates: Coordinate[][][]): void {
        this.geometry.setCoordinates(coordinates);
    }

    public intersectsExtent(extent: [number, number, number, number]) {
        return this.geometry.intersectsExtent(extent);
    }

    public getSingleGeometry() {
        return new Polygon(this.geometry.getCoordinates()[0]);
    }
    
    public adjustGeometryToBeInserted(geometry: Geometry, path: number[]) {
        // Note: here we try to convert source geometry to a linear ring and checks if it is inside the destination geometry
        var geometryRing = GeometryExtensionUtilities.isLinearRing(geometry);

        if (geometryRing) {
            const containsAll = (ring: LinearRing, i: number) => {
                const polygon = this.geometry.getPolygon(i);
                if (polygon) 
                    return ring.getCoordinates().every(c => GeometryExtensionUtilities.containsCoordinate(polygon, c));
                return false;
            }

            // We want to check geometry in path first
            if (path[0] !== undefined && containsAll(geometryRing, path[0])) {
                geometry = geometryRing;
            }
            else {
                for (var i = 0; i < this.geometry.getPolygons().length; i++) {
                    if (containsAll(geometryRing, i)) {
                        geometry = geometryRing;
                        path[0] = i;

                        // prevent setting internal ring at index 0;
                        if (!path[1])
                            path[1] = 1;

                        break;
                    }
                }
            }
        }

        return { geometry, path };
    }   

    public insertGeometry(geometry: Geometry, path: number[]): number[] | undefined {
        const coords = this.getCoordinates() ?? [];

        if (geometry instanceof Polygon) {            
            const position = GeometryExtensionUtilities.getPositionFromPath1(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyMultiLineInMultiPoly(position, coords, geometry.getCoordinates()));
            return position;
        }
        if (geometry instanceof LinearRing) {
            const position = GeometryExtensionUtilities.getPositionFromPath2(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyLineInMultiPoly(position, coords, geometry.getCoordinates()));
            return position;
        }
        else if (geometry instanceof Point) {
            const position = GeometryExtensionUtilities.getPositionFromPath3(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyPointInMultiPoly(position, coords, geometry.getCoordinates()));
            return position;
        }
        return undefined
    }

    public replaceGeometry(geometry: Geometry, path: number[]): number[] | undefined {
        const coords = this.getCoordinates() ?? [];

        if (geometry instanceof MultiPolygon) {
            this.setCoordinates(geometry.getCoordinates().slice())
            return [];
        }
        else if (geometry instanceof Polygon) {
            const position = GeometryExtensionUtilities.getPositionFromPath1(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyMultiLineInMultiPoly(position, coords, geometry.getCoordinates(), true));
            return position;
        }
        else if (geometry instanceof LineString) {
            const position = GeometryExtensionUtilities.getPositionFromPath2(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyLineInMultiPoly(position, coords, geometry.getCoordinates(), true));
            return position;
        }
        else if (geometry instanceof Point) {
            const position = GeometryExtensionUtilities.getPositionFromPath3(path);
            const newCoords = GeometryExtensionUtilities.modifyPointInMultiPoly(position, coords, geometry.getCoordinates(), true);
            const [poly, ring, point] = position;
            GeometryExtensionUtilities.fixLinearRing(newCoords[poly][ring], point === 0 ? 'first' : 'last');
            this.setCoordinates(newCoords);
            return position;
        }

        return undefined
    }

    public removeGeometry(path: number[]): number[] | undefined {
        const coords = this.getCoordinates() ?? [];

        if (path.length >= 3 && coords[path[0]][path[1]][path[2]]) {
            const position = GeometryExtensionUtilities.getPositionFromPath3(path);
            const newCoords = GeometryExtensionUtilities.modifyPointInMultiPoly(position, coords);
            const [poly, ring, point] = position;
            GeometryExtensionUtilities.fixLinearRing(newCoords[poly][ring], point === 0 ? 'first' : 'last');
            this.setCoordinates(newCoords);
            return position;
        }
        else if (path.length >= 2 && coords[path[0]][path[1]]) {
            const position = GeometryExtensionUtilities.getPositionFromPath2(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyLineInMultiPoly(position, coords));
            return position;
        }
        else if (path.length >= 1 && coords[path[0]]) {
            const position = GeometryExtensionUtilities.getPositionFromPath1(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyMultiLineInMultiPoly(position, coords));
            return position;
        }

        return undefined;
    }

    public canRemove(path: number[]): boolean {
        const coords = this.getCoordinates() ?? [];

        if (path.length >= 1) {
            const poly = coords[path[0]];
            if (poly) {
                //remove poly
                if (path.length === 1)
                    return coords.length > 1;
                else {
                    const ring = poly[path[1]]
                    if (ring) {
                        //remove ring
                        if (path.length === 2)
                            return poly.length > 1;
                        //remove point
                        else
                            return ring.length > 4 && !!ring[path[2]];
                    }
                }
            }
        }

        return false;
    }

    public groupSectionsAtDateLine() {
        const coords = this.geometry.getPolygons().map(polygon => {
            return polygon.getLinearRings().map(line => {
                const coordinate = getCenter(line.getExtent());
                if (GeometryExtensionUtilities.shouldWrapLongitude(coordinate[0]))
                    line.translate(DEGREES_360, 0);
                return line.getCoordinates();
            })
        })
        this.setCoordinates(coords);
    }

    public mergeSnappedPoints() {
        var mergedSnapped = false;

        const result: Coordinate[][][] = [];
        const polygons = this.getCoordinates();

        for (var polygon of polygons) {
            for (var i = 0; i < polygon.length; i++) {
                let ring = polygon[i];
                if (ring.length > 3) {
                    let merged = GeometryExtensionUtilities.mergeSnappedPoints(ring);
                    if (merged) {
                        mergedSnapped = true;
                        polygon[i] = merged;
                    }                    
                }                
            }
            result.push(polygon);
        }     

        if (mergedSnapped)
            this.setCoordinates(result);

        return mergedSnapped;
    }
}